import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { supabase } from '../../../lib/supabase';
import { resolveIllustrationSource } from '../../../data/plumbingIllustrations';
import PromptStep from './PromptStep';
import StepActions from './StepActions';

const CategoryStep = ({ data, onUpdate, onNext, onBack, isActive = true, showBack = true }) => {
  const [selectedCategory, setSelectedCategory] = useState(data?.category || null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: categoriesData, error: categoriesError } = await supabase
        .from('category')
        .select('*')
        .eq('actif', true)
        .order('order');

      if (categoriesError) throw categoriesError;

      const transformedCategories = (categoriesData || []).map(category => {
        let imageUrl = "https://images.unsplash.com/photo-1581094794329-c8112a89af12";
        if (category.Image && Array.isArray(category.Image) && category.Image.length > 0) {
          imageUrl = category.Image[0] || imageUrl;
        }

        const name = category.name || 'Sans nom';

        return {
          id: category.id,
          name,
          icon: resolveIllustrationSource(category.logo, name),
          image: imageUrl,
          imageAlt: category.description || `${category.name} category`,
        };
      });

      setCategories(transformedCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message || 'Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

 const handleCategorySelect = (category) => {
  setSelectedCategory(category);
  handleSubmit(category);
};

const handleSubmit = (categoryParam) => {
  const categoryToSubmit = categoryParam || selectedCategory;
  if (!categoryToSubmit) return;

  onUpdate({
    category: categoryToSubmit
  });
  onNext(true);
};

  const handlePromptNext = (promptData) => {
    setShowPromptModal(false);
    
    // Mettre à jour les données avec le prompt
    onUpdate({
      ...promptData,
      category: selectedCategory
    });
    
    // Passer à l'étape suivante si une catégorie est sélectionnée
    if (selectedCategory) {
      onNext(true);
    }
  };

  const handlePromptSkip = () => {
    setShowPromptModal(false);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Chargement des catégories...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
              <p className="text-error font-medium mb-4">{error}</p>
              <Button onClick={fetchCategories} variant="outline">
                Réessayer
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              Où se situe le problème ?
            </h2>
            <p className="text-muted-foreground">
              Dans quelle pièce devons-nous intervenir ?
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => handleCategorySelect(category)}
                className={`group relative overflow-hidden rounded-lg border-2 transition-all duration-200 p-4 flex items-center gap-3 text-left md:flex-col md:items-center md:text-center md:p-6 ${
                  selectedCategory?.id === category?.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 bg-card'
                }`}
              >
                <div className="w-5 h-5 flex-shrink-0 md:w-20 md:h-20 md:mb-4 flex items-center justify-center">
                  <Image
                    src={category?.icon}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="font-medium text-sm flex-1 md:flex-none md:text-lg">{category?.name}</span>
                {selectedCategory?.id === category?.id && (
                  <Icon
                    name="CheckCircle"
                    size={20}
                    color="var(--color-primary)"
                    className="flex-shrink-0 md:mt-2"
                  />
                )}
              </button>
            ))}
          </div>

          <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-8 mt-8 border-t border-border">
            {/* <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
              Retour
            </Button> */}
            {/* <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={handleSubmit}
              disabled={!selectedCategory}
            >
              Continuer
            </Button> */}
          </StepActions>
        </div>
      </div>

      {/* Modal pour l'étape Prompt */}
      {showPromptModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-black bg-opacity-50" 
              onClick={() => setShowPromptModal(false)}
            />

            {/* Modal */}
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-20 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-card px-6 pt-6 pb-4">
                {/* En-tête de la modal */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-primary flex items-center justify-center">
                      <Icon name="Sparkles" size={20} color="white" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">
                      Décrivez votre problème
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowPromptModal(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                {/* Contenu de l'étape Prompt */}
                <div className="max-h-[70vh] overflow-y-auto">
                  <PromptStep
                    data={data}
                    onUpdate={handlePromptNext}
                    onNext={handlePromptNext}
                    onBack={() => setShowPromptModal(false)}
                    onSkipToWizard={handlePromptSkip}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryStep;